import React, { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { ViewHeatmapBtn } from './PhenoScanSubTab';
import ValidationPanel from '@/components/ValidationPanel';
import {
  Upload, Image as ImageIcon, Download, Play, Check, AlertTriangle,
  Copy, Trees, Leaf, Ruler, FlaskConical, BarChart3,
} from 'lucide-react';

const LEAF_GROUND_TRUTH_CM2 = 15.23;

export default function LeafCanopy() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isCanopy, setIsCanopy] = useState(false);
  const [arucoCm, setArucoCm] = useState(3.0);
  const [coinCm, setCoinCm] = useState(2.3);
  const [groundTruth, setGroundTruth] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
    setResult(null); setOverlay(null); setElapsedMs(null);
    setGroundTruth('');
  };

  const loadSample = async () => {
    setBusy(true);
    try {
      const [imgR, metaR] = await Promise.all([
        axios.get(`${API}/phenoscan/leaf/sample`, { responseType: 'blob' }),
        axios.get(`${API}/phenoscan/leaf/sample-meta`),
      ]);
      const blob = imgR.data;
      const f = new File([blob], metaR.data.filename || 'leaf_sample.png', { type: 'image/png' });
      setImageFile(f);
      setImagePreview(URL.createObjectURL(blob));
      setGroundTruth(String(metaR.data.ground_truth_area_cm2 ?? LEAF_GROUND_TRUTH_CM2));
      setArucoCm(3.0);
      setResult(null); setOverlay(null); setElapsedMs(null);
      toast.success(`Sample loaded — ground truth ${metaR.data.ground_truth_area_cm2} cm²`);
    } catch (err) {
      toast.error('Failed to load sample image');
    } finally {
      setBusy(false);
    }
  };

  const runAnalysis = async () => {
    if (!imageFile) { toast.error('Pick an image first'); return; }
    setBusy(true); setResult(null); setOverlay(null);
    const t0 = performance.now();
    try {
      const fd = new FormData();
      fd.append('file', imageFile);
      fd.append('is_canopy', String(isCanopy));
      fd.append('aruco_marker_cm', String(arucoCm));
      fd.append('coin_diameter_cm', String(coinCm));
      if (groundTruth) fd.append('ground_truth_area_cm2', String(groundTruth));
      const r = await axios.post(`${API}/phenoscan/leaf/analyze`, fd);
      const data = r.data.results;
      setResult(data);
      setOverlay(data.overlay_png_base64 ? `data:image/png;base64,${data.overlay_png_base64}` : null);
      setElapsedMs(Math.round(performance.now() - t0));
      toast.success('Analysis complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setBusy(false);
    }
  };

  const exportAs = async (fmt) => {
    if (!result) return;
    try {
      const r = await axios.post(`${API}/phenoscan/export?fmt=${fmt}`,
        { analysis: 'leaf', results: result }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `phenoscan_leaf.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const copySettings = () => {
    const cfg = {
      module: 'phenoscan_leaf_canopy',
      is_canopy: isCanopy,
      aruco_marker_cm: arucoCm,
      coin_diameter_cm: coinCm,
      segmentation: 'ExG + Otsu + morphology',
      version: 'bkagrid-phenoscan-1.0',
    };
    navigator.clipboard?.writeText(JSON.stringify(cfg, null, 2));
    toast.success('Settings copied to clipboard');
  };

  const traitMatrixForHandoff = useMemo(() => {
    if (!result) return null;
    return {
      source: 'phenoscan_leaf',
      traits: {
        area_cm2: result.shape?.area_cm2,
        length_cm: result.shape?.length_cm,
        width_cm: result.shape?.width_cm,
        aspect_ratio: result.shape?.aspect_ratio,
        circularity: result.shape?.circularity,
        SPAD_proxy: result.chlorophyll?.SPAD_proxy,
        GI: result.chlorophyll?.GI,
        NDGI: result.chlorophyll?.NDGI,
        pct_yellowing: result.stress?.pct_yellowing,
        pct_necrosis: result.stress?.pct_necrosis,
      },
      lai: result.lai,
    };
  }, [result]);

  const handoff = useCallback((targetPath, analysisKey) => {
    if (!traitMatrixForHandoff) {
      toast.error('Run an analysis first'); return;
    }
    navigate(targetPath, { state: { phenoscanHandoff: { ...traitMatrixForHandoff, target: analysisKey } } });
    toast.success('Trait matrix handed off — no re-upload needed');
  }, [navigate, traitMatrixForHandoff]);

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6" data-testid="leaf-canopy-pane">
      {/* Left: Upload & Controls */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>1. Image</h3>

        <div className="rounded-xl p-4 mb-3 text-center cursor-pointer"
          style={{ border: '1px dashed #BCC5B5', background: '#FAFBF9' }}
          onClick={() => fileRef.current?.click()}
          data-testid="leaf-upload-zone"
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Upload" style={{ maxHeight: 180, margin: '0 auto', borderRadius: 8 }} />
          ) : (
            <div className="py-6 text-sm" style={{ color: '#6F695F' }}>
              <Upload size={22} className="mx-auto mb-2" />
              Click or drag an image (JPG/PNG/TIFF)
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={onPickFile} data-testid="leaf-upload-input" />
        </div>

        <button onClick={loadSample} disabled={busy}
          className="w-full rounded-lg py-2 text-sm mb-5 transition"
          style={{ background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' }}
          data-testid="leaf-load-sample-btn">
          <ImageIcon size={14} className="inline mr-2" />
          Load Sample (known area 15.23 cm²)
        </button>

        <h3 className="font-display text-lg mb-3" style={{ color: '#1B4332' }}>2. Parameters</h3>

        <label className="flex items-center gap-2 mb-3 text-sm">
          <input type="checkbox" checked={isCanopy} onChange={e => setIsCanopy(e.target.checked)}
            data-testid="leaf-canopy-toggle" />
          <Trees size={14} /> Canopy photo (enable LAI)
        </label>

        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>
            <Ruler size={12} className="inline mr-1" /> ArUco marker side (cm)
          </div>
          <input type="number" step="0.1" min="0.5" value={arucoCm}
            onChange={e => setArucoCm(parseFloat(e.target.value) || 0)}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="leaf-aruco-cm" />
        </label>

        <label className="block mb-3 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Coin diameter fallback (cm)</div>
          <input type="number" step="0.1" min="0.5" value={coinCm}
            onChange={e => setCoinCm(parseFloat(e.target.value) || 0)}
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="leaf-coin-cm" />
        </label>

        <label className="block mb-4 text-sm">
          <div className="mb-1" style={{ color: '#4F4B44' }}>Ground truth area (cm², optional)</div>
          <input type="number" step="0.01" value={groundTruth}
            onChange={e => setGroundTruth(e.target.value)}
            placeholder="e.g. 15.23"
            className="w-full rounded px-3 py-1.5"
            style={{ border: '1px solid #E5E5E0', background: '#fff' }}
            data-testid="leaf-ground-truth" />
        </label>

        <button onClick={runAnalysis} disabled={busy || !imageFile}
          className="w-full rounded-lg py-2.5 text-sm font-semibold transition disabled:opacity-50"
          style={{ background: '#1B4332', color: '#fff' }}
          data-testid="leaf-run-btn">
          <Play size={14} className="inline mr-2" /> {busy ? 'Running…' : 'Run Leaf Analysis'}
        </button>

        <button onClick={copySettings} disabled={busy}
          className="w-full rounded-lg py-2 text-xs mt-2"
          style={{ background: '#F3F6F3', color: '#4F4B44', border: '1px solid #E5E5E0' }}
          data-testid="leaf-copy-settings">
          <Copy size={12} className="inline mr-2" /> Copy Settings (reproducibility)
        </button>
      </div>

      {/* Right: Results */}
      <div className="rounded-2xl p-5 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        {!result ? (
          <div className="text-center py-16">
            <Leaf size={28} className="mx-auto mb-3" style={{ color: '#BCC5B5' }} />
            <div className="font-display text-lg" style={{ color: '#6F695F' }}>No results yet</div>
            <div className="text-sm mt-1" style={{ color: '#8A8580' }}>
              Upload an image or click <b>Load Sample</b> to see a validated end-to-end run.
            </div>
          </div>
        ) : (
          <div data-testid="leaf-results">
            {/* Header row: calibration, status, elapsed */}
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <CalBadge cal={result.calibration} />
              {result.ground_truth && <GTBadge gt={result.ground_truth} />}
              {elapsedMs != null && (
                <span className="px-2.5 py-1 rounded-full text-xs"
                  style={{ background: '#F3F6F3', color: '#4F4B44' }}>
                  ⏱ {elapsedMs} ms (target &lt; 5000)
                </span>
              )}
              <div className="ml-auto flex gap-1">
                <ExportBtn onClick={() => exportAs('csv')}>CSV</ExportBtn>
                <ExportBtn onClick={() => exportAs('xlsx')}>XLSX</ExportBtn>
                <ExportBtn onClick={() => exportAs('pdf')}>PDF</ExportBtn>
                {imageFile && <ViewHeatmapBtn file={imageFile} kind="leaf_greenness"
                  colormap="RdYlGn" navigate={navigate}
                  title={`Leaf greenness · ${imageFile.name}`}
                  pixelsPerCm={result?.calibration?.pixels_per_cm} />}
                {overlay && (
                  <a href={overlay} download="leaf_overlay.png"
                    className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
                    style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#4F4B44' }}
                    data-testid="leaf-export-png">
                    <Download size={11} /> PNG
                  </a>
                )}
              </div>
            </div>

            {/* Ground truth box */}
            {result.ground_truth && <GroundTruthBox gt={result.ground_truth} />}

            {/* Image preview + overlay */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <ImgPanel title="Original" src={imagePreview} />
              <ImgPanel title="Detected leaf (overlay)" src={overlay} testid="leaf-overlay-img" />
            </div>

            {/* Trait tables */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard title="Shape" icon={Ruler} rows={kvPairs(result.shape)} />
              <StatCard title="Chlorophyll proxies" icon={FlaskConical} rows={kvPairs(result.chlorophyll)} />
              <StatCard title="Stress scoring" icon={AlertTriangle} rows={kvPairs(result.stress)} />
              <StatCard title="Color (RGB / HSV / Lab)" icon={BarChart3}
                rows={[...kvPairs(result.color?.rgb, 'rgb.'),
                  ...kvPairs(result.color?.hsv, 'hsv.'),
                  ...kvPairs(result.color?.lab, 'lab.')]} />
              {result.lai != null && (
                <StatCard title="Canopy" icon={Trees} rows={[['LAI (Beer-Lambert, k=0.5)', result.lai]]} />
              )}
              <MethodCard method={result.method} parameters={result.parameters} />
            </div>

            {/* Hand-offs */}
            <div className="rounded-xl p-4 mt-5"
              style={{ background: '#FAFBF9', border: '1px solid #E5E5E0' }} data-testid="leaf-handoff">
              <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#6F695F' }}>
                Next step — hand off to BKAgriD statistics
              </div>
              <div className="flex flex-wrap gap-2">
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'path_analysis')}>Path Analysis</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'pca')}>PCA</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'd_squared')}>D² & Cluster</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'heritability')}>Heritability</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'selection_index')}>Selection Index</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'ammi')}>AMMI</HandoffBtn>
                <HandoffBtn onClick={() => handoff('/biometrical-analysis', 'gge_biplot')}>GGE Biplot</HandoffBtn>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="lg:col-span-2">
        <ValidationPanel subTab="leaf" subTabLabel="Leaf & Canopy" />
      </div>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────

function CalBadge({ cal }) {
  if (!cal) return null;
  const ok = cal.calibrated;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#ECFDF5' : '#FEF3C7', color: ok ? '#065F46' : '#92400E' }}
      data-testid="leaf-cal-badge">
      {ok ? <Check size={11} /> : <AlertTriangle size={11} />}
      {ok ? `Calibrated: ${cal.method} — ${cal.pixels_per_cm} px/cm` : 'UNCALIBRATED — relative units only'}
    </span>
  );
}

function GTBadge({ gt }) {
  const ok = gt.status === 'OK';
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
      style={{ background: ok ? '#EFF6FF' : '#FEF3C7', color: ok ? '#1E40AF' : '#92400E' }}
      data-testid="leaf-gt-badge">
      Measured {gt.measured_cm2} cm² vs GT {gt.ground_truth_cm2} cm² · Δ {gt.diff_pct}%
    </span>
  );
}

function GroundTruthBox({ gt }) {
  const ok = gt.status === 'OK';
  return (
    <div className="rounded-xl p-3 mb-4 grid grid-cols-3 gap-3"
      style={{ background: ok ? '#F0FDF4' : '#FFFBEB',
        border: `1px solid ${ok ? '#BBF7D0' : '#FDE68A'}` }}
      data-testid="leaf-gt-box">
      <div><div className="text-xs" style={{ color: '#6F695F' }}>BKAgriD measured</div>
        <div className="font-display text-xl" style={{ color: '#1B4332' }}>{gt.measured_cm2} cm²</div></div>
      <div><div className="text-xs" style={{ color: '#6F695F' }}>Ground truth</div>
        <div className="font-display text-xl" style={{ color: '#1B4332' }}>{gt.ground_truth_cm2} cm²</div></div>
      <div><div className="text-xs" style={{ color: '#6F695F' }}>Difference</div>
        <div className="font-display text-xl" style={{ color: ok ? '#065F46' : '#92400E' }}>
          {gt.diff_pct}% {ok ? '✓' : '⚠'}
        </div></div>
    </div>
  );
}

function ImgPanel({ title, src, testid }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #E5E5E0' }}>
      <div className="text-xs px-3 py-1.5" style={{ background: '#F3F6F3', color: '#4F4B44' }}>{title}</div>
      <div className="bg-white flex items-center justify-center" style={{ height: 260 }}>
        {src ? <img data-testid={testid} src={src} alt={title} style={{ maxHeight: 260, maxWidth: '100%' }} /> :
          <div className="text-sm" style={{ color: '#BCC5B5' }}>—</div>}
      </div>
    </div>
  );
}

function StatCard({ title, icon: Icon, rows }) {
  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid #E5E5E0', background: '#FFFFFF' }}>
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wide" style={{ color: '#4F4B44' }}>
        {Icon && <Icon size={12} />} {title}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px dashed #F0EFEA' }}>
              <td style={{ color: '#6F695F', padding: '4px 6px 4px 0' }}>{k}</td>
              <td style={{ color: '#1B4332', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {v == null ? '—' : String(v)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodCard({ method, parameters }) {
  return (
    <div className="rounded-xl p-3" style={{ border: '1px solid #E5E5E0', background: '#FFFFFF' }}>
      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: '#4F4B44' }}>Method &amp; Parameters</div>
      {method && Object.entries(method).map(([k, v]) => (
        <div key={k} className="text-xs mb-1"><b>{k}:</b> <span style={{ color: '#4F4B44' }}>{String(v)}</span></div>
      ))}
      {parameters && Object.entries(parameters).map(([k, v]) => (
        <div key={k} className="text-xs mb-1" style={{ color: '#6F695F' }}>{k}: {String(v)}</div>
      ))}
    </div>
  );
}

function HandoffBtn({ children, onClick }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-md text-xs transition"
      style={{ background: '#fff', border: '1px solid #2D6A4F', color: '#1B4332' }}
      data-testid={`leaf-handoff-${String(children).toLowerCase().replace(/\s+/g,'-').replace(/[²]/g,'2')}`}>
      {children} →
    </button>
  );
}

function ExportBtn({ onClick, children }) {
  return (
    <button onClick={onClick} className="px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"
      style={{ background: '#F3F6F3', border: '1px solid #E5E5E0', color: '#4F4B44' }}
      data-testid={`leaf-export-${String(children).toLowerCase()}`}>
      <Download size={11} /> {children}
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function kvPairs(obj, prefix = '') {
  if (!obj) return [];
  const pairs = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      for (const [kk, vv] of Object.entries(v)) pairs.push([`${prefix}${k}.${kk}`, vv]);
    } else {
      pairs.push([`${prefix}${k}`, v]);
    }
  }
  return pairs;
}
